export interface ResolveHdeStepIndexOptions {
    /** The duplicate-registration gate should be shown and must be acknowledged. */
    showExistingSubmissionGate: boolean;
    /** A submit has been fired this session. */
    hasSubmitted: boolean;
    /** A submit has succeeded this session. */
    isSubmitSuccess: boolean;
}

/**
 * Chooses the step index after `visibleSteps` changes. Logged-in members must land on
 * the existing-submission gate when it first appears; after submit starts, re-map by
 * step id so a late lookup cannot shift the success screen back to guest-invitation.
 */
export function resolveHdeStepIndexAfterVisibleStepsChange(
    previousStepIds: readonly string[],
    currentStepIds: readonly string[],
    currentStepIndex: number,
    options: ResolveHdeStepIndexOptions,
): number | null {
    const existingGateIndex = currentStepIds.indexOf('existing-submission');
    const gateNewlyVisible =
        options.showExistingSubmissionGate &&
        existingGateIndex >= 0 &&
        !previousStepIds.includes('existing-submission');

    if (gateNewlyVisible) {
        return existingGateIndex;
    }

    const gateDismissed =
        previousStepIds.includes('existing-submission') &&
        !currentStepIds.includes('existing-submission');

    // After the member resolves the gate (update, switch type, etc.), restart at the
    // first form step — otherwise a stale index can land on company details when the
    // gate sat after event-selection in multi-event pro/exhibitor flows.
    if (gateDismissed && !options.hasSubmitted && !options.isSubmitSuccess) {
        return 0;
    }

    if (options.hasSubmitted || options.isSubmitSuccess) {
        return realignHdeStepIndexAfterVisibleStepsChange(
            previousStepIds,
            currentStepIds,
            currentStepIndex,
        );
    }

    return null;
}

/**
 * When HDE registration `visibleSteps` gains or loses conditional steps (e.g.
 * existing-submission gate), step indices shift but `currentStepIndex` is unchanged.
 * Re-map by step id so the user stays on the same screen (e.g. success, not guest).
 */
export function realignHdeStepIndexAfterVisibleStepsChange(
    previousStepIds: readonly string[],
    currentStepIds: readonly string[],
    currentStepIndex: number,
): number | null {
    if (
        previousStepIds.length === 0 ||
        previousStepIds.length === currentStepIds.length ||
        currentStepIndex < 0 ||
        currentStepIndex >= previousStepIds.length
    ) {
        return null;
    }

    const anchoredStepId = previousStepIds[currentStepIndex];
    if (anchoredStepId === undefined) {
        return null;
    }

    const realignedIndex = currentStepIds.indexOf(anchoredStepId);
    if (realignedIndex >= 0 && realignedIndex !== currentStepIndex) {
        return realignedIndex;
    }

    return null;
}
